import { useEffect, useState } from 'react';

/**
 * Whether a CSS media query currently matches, kept in sync as the viewport
 * changes.
 *
 * For anything a stylesheet can express, use a Tailwind breakpoint variant
 * instead — this is for the cases CSS cannot reach: a value that has to arrive
 * in JavaScript, such as an SVG chart's axis width, or a layout decision a
 * component makes rather than paints.
 *
 * Phrase queries as `min-width` wherever there is a choice. jsdom has no
 * `matchMedia`, so `tests/setup.ts` stubs one that reports every query as
 * matching; a `min-width` query therefore lands tests on the desktop branch,
 * which is the layout the suite asserts.
 */
export const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia(query).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    // Read once here too: the value captured during the first render can
    // already be stale by the time effects run.
    setMatches(mql.matches);
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
};
